/** Long enough for an OAuth redirect to return, short enough that a later login is not a signup. */
export const GA_SIGNUP_WINDOW_MS = 10 * 60 * 1000;

/** Client clocks sit this far behind Clerk without the account being treated as future. */
export const GA_SIGNUP_CLOCK_SKEW_MS = 2 * 60 * 1000;

const STORAGE_PREFIX = 'ga-sign-up:';

/** Clerk user ids already reported in this tab. localStorage covers other tabs and reloads. */
const reportedSignups = new Set<string>();

export function isRecentSignup(createdAtMs: number, now: number, windowMs = GA_SIGNUP_WINDOW_MS, skewMs = GA_SIGNUP_CLOCK_SKEW_MS) {
    const age = now - createdAtMs;
    return age >= -skewMs && age <= windowMs;
}

/**
 * How long to wait before a `createdAt` that is still ahead of the client clock
 * falls inside the skew allowance. Null when the account is already eligible,
 * or when waiting that long would put it outside the signup window.
 */
export function signupRetryDelayMs(createdAtMs: number, now: number, windowMs = GA_SIGNUP_WINDOW_MS, skewMs = GA_SIGNUP_CLOCK_SKEW_MS) {
    const age = now - createdAtMs;
    if (age >= -skewMs) return null;
    if (createdAtMs - now > windowMs) return null;
    return -age - skewMs;
}

/**
 * GA4 `sign_up.method` values. The key is the Clerk provider id (`google`,
 * `discord`); email sign-up has no provider, so its key is `email`.
 * The value is the string GA4 stores. Add a row to name a new source —
 * Reddit, another OAuth provider — instead of changing the event call.
 * A provider with no row is reported under its own id, so a signup still counts
 * before the row exists.
 */
export const GA_SIGN_UP_METHODS = {
    email: 'email',
    google: 'google',
    discord: 'discord',
} as const;

const signUpMethodLabels: Record<string, string> = GA_SIGN_UP_METHODS;

/**
 * GA4 method for this account. Null when there is no external provider yet and
 * no password: an OAuth account can appear before `externalAccounts` is filled,
 * and reporting that as email would stick once the signup is marked sent.
 */
export function signUpMethod(provider: string | undefined, passwordEnabled: boolean) {
    if (provider && provider.length > 0) return signUpMethodLabels[provider] ?? provider;
    if (passwordEnabled) return GA_SIGN_UP_METHODS.email;
    return null;
}

export function signupStorageKey(userId: string) {
    return `${STORAGE_PREFIX}${userId}`;
}

export function hasReportedSignup(userId: string, storage: Pick<Storage, 'getItem'> | null) {
    if (reportedSignups.has(userId)) return true;
    try {
        return storage?.getItem(signupStorageKey(userId)) === '1';
    } catch {
        return false;
    }
}

export function markSignupReported(userId: string, storage: Pick<Storage, 'setItem'> | null) {
    reportedSignups.add(userId);
    try {
        storage?.setItem(signupStorageKey(userId), '1');
    } catch {
        // Private mode or a blocked storage API. The in-memory set still covers this page session.
    }
}

export function signupLockName(userId: string) {
    return `ga-sign-up:${userId}`;
}

/**
 * Sends at most once. A failed send leaves the user unmarked so a later attempt
 * (script still downloading, collection turned back on) can try again.
 */
export function sendSignupOnce(userId: string, storage: Pick<Storage, 'getItem' | 'setItem'> | null, send: () => boolean) {
    if (hasReportedSignup(userId, storage)) return false;
    if (!send()) return false;
    markSignupReported(userId, storage);
    return true;
}

interface SignupLock {
    request: (name: string, callback: () => void) => Promise<unknown>;
}

/**
 * Runs `task` in a Web Lock so two tabs cannot both pass the storage check
 * before either writes it. Without `navigator.locks`, `task` runs immediately.
 */
export function runWithSignupLock(userId: string, task: () => void, locks: SignupLock | null = browserLocks()) {
    if (!locks) {
        task();
        return;
    }
    void locks.request(signupLockName(userId), task);
}

function browserLocks(): SignupLock | null {
    if (typeof navigator === 'undefined' || !navigator.locks) return null;
    const locks = navigator.locks;
    return {
        request: (name, callback) => locks.request(name, callback),
    };
}
